import type { PermissionKey } from "@how/types";

/**
 * Custom roles from per-module levels (ADR 0054). A church builds a role by choosing, per area, `none`, `view`,
 * `edit` or `manage`; the level compiles to permissions here, once, reviewed like a permission. Levels are cumulative:
 * `edit` holds everything `view` holds, `manage` everything `edit` holds. Mirrored to `public.permission_levels` by
 * `pnpm catalog:generate`, which is what `set_custom_role` reads.
 *
 * An area is a module key, or `communications`, whose permissions have no owning module. Administration of the church
 * itself (settings, users, audit, exports, imports, keys, webhooks, integrations, refunds, campuses, sender identities,
 * sermons, workflows) is in no level: a custom role is below administrator by design.
 */
export type RoleLevel = "view" | "edit" | "manage";
export const ROLE_LEVELS: readonly RoleLevel[] = ["view", "edit", "manage"];

export interface AreaLevels {
  readonly area: string;
  readonly displayName: string;
  readonly view: readonly PermissionKey[];
  readonly edit: readonly PermissionKey[];
  readonly manage: readonly PermissionKey[];
}

const area = (
  key: string,
  displayName: string,
  view: readonly PermissionKey[],
  edit: readonly PermissionKey[],
  manage: readonly PermissionKey[],
): AreaLevels => ({ area: key, displayName, view, edit, manage });

/** What each level adds, per area. Read `levelPermissions()` for the cumulative set. */
export const MODULE_LEVELS: readonly AreaLevels[] = [
  area(
    "core_crm",
    "People",
    ["people.read", "notes.read", "care.read"],
    ["people.write", "notes.write", "households.manage", "tags.manage", "preferences.manage"],
    ["people.merge", "custom_fields.manage", "care.manage"],
  ),
  area(
    "groups",
    "Groups and serving",
    ["groups.read"],
    ["groups.read_assigned", "groups.manage_assigned"],
    ["groups.manage", "serving.manage"],
  ),
  area("events", "Events", ["events.read"], ["registrations.manage"], ["events.manage"]),
  area("event_checkin", "Check-in", [], ["events.check_in"], []),
  area("services", "Services", ["services.read", "services.respond"], ["services.manage"], []),
  area("membership", "Membership", ["membership.read"], ["membership.manage"], []),
  area(
    "communications",
    "Communications",
    ["communications.read"],
    ["communications.compose"],
    ["communications.send", "communications.approve", "communications.inbox"],
  ),
  area(
    "website",
    "Website",
    ["website.read"],
    ["website.manage", "forms.manage", "media.manage"],
    ["website.publish"],
  ),
  area(
    "commerce",
    "Store",
    ["commerce.read"],
    ["commerce.fulfill_orders"],
    ["commerce.manage_catalog"],
  ),
  area("advanced_reporting", "Reports", ["reports.advanced"], [], ["reports.export"]),
];

/**
 * The only sensitive permissions a custom role may carry, each an explicit switch a church turns on on purpose:
 * children's records (D1), confidential notes (D3), confidential care and conversations (D13, D21).
 */
export const CUSTOM_ROLE_EXTRAS: readonly PermissionKey[] = [
  "people.read_minors",
  "notes.read_confidential",
  "care.read_confidential",
];

/** Every custom role holds it: it is what says "a member of this church" to `private.authorize()`. */
export const CUSTOM_ROLE_ALWAYS: readonly PermissionKey[] = ["portal.access"];

/** The cumulative permissions of a level in an area; `none` (or an unknown area) is empty. */
export function levelPermissions(
  areaKey: string,
  level: RoleLevel | "none",
): readonly PermissionKey[] {
  const found = MODULE_LEVELS.find((a) => a.area === areaKey);
  if (found === undefined || level === "none") return [];
  const out: PermissionKey[] = [...found.view];
  if (level === "edit" || level === "manage") out.push(...found.edit);
  if (level === "manage") out.push(...found.manage);
  return out;
}

/** Rows for `public.permission_levels`: one per (area, level, permission), cumulative. */
export function permissionLevelRows(): readonly {
  readonly area: string;
  readonly level: RoleLevel;
  readonly permission: PermissionKey;
}[] {
  const rows: { area: string; level: RoleLevel; permission: PermissionKey }[] = [];
  for (const a of MODULE_LEVELS) {
    for (const level of ROLE_LEVELS) {
      for (const permission of levelPermissions(a.area, level))
        rows.push({ area: a.area, level, permission });
    }
  }
  return rows;
}
